import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date string to a human-readable relative time
 * Examples: "just now", "5 minutes ago", "2 hours ago", "3 days ago", "Jan 15, 2026"
 */
export function formatTimeAgo(dateString: string | undefined): string {
  if (!dateString) return 'unknown';
  
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  // Future dates or invalid
  if (seconds < 0 || isNaN(seconds)) {
    return formatDate(dateString);
  }
  
  // Less than a minute
  if (seconds < 60) {
    return 'just now';
  }
  
  // Less than an hour
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  }
  
  // Less than a day
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  }
  
  // Less than a week
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }
  
  // Less than a month
  const weeks = Math.floor(days / 7);
  if (weeks < 4) {
    return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  }
  
  // Older than a month - show formatted date
  return formatDate(dateString);
}

/**
 * Format a date string to a readable date (e.g., "Jan 15, 2026")
 */
export function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'unknown';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'unknown';
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date string to a compact relative time (e.g., "2m", "15m", "1h", "2d")
 */
export function formatCompactTime(dateString: string | undefined): string {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (seconds < 0 || isNaN(seconds)) return '';
  
  if (seconds < 60) return 'now';
  
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  
  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}
